// Share landing page (docs/specs/sharing task 009) — the recipient's entry
// point at `/share/:token`, fully public (no session, no AppLayout).
//
// State machine, driven by exactly the spec's request budget (criterion 8:
// resolve, unlock, items — nothing else, no polling):
//
//   mount → GET resolve
//     ├─ 410 → terminal "no longer available" (sticky, no retry)
//     ├─ 404 → terminal "doesn't exist" variant (sticky, no retry)
//     ├─ other error → generic ErrorState with retry (network/5xx can be
//     │   transient; 410/404 never are, hence the split)
//     ├─ locked → password gate; POST unlock only on submit
//     │    ├─ 401 → inline "Wrong password.", stay on the form
//     │    ├─ 429 → inline try-later state, stay on the form
//     │    ├─ other error → inline server error, stay on the form
//     │    └─ 204 → flip cached resolve to unlocked, GET items → grid
//     └─ unlocked → GET items → folder browse (share-folders task 005)
//
// The items fetch is the ONE tree read (share-folders spec criterion 1):
// the payload carries every folder and every item's placement, and all
// navigation afterward is client state — the current level is `?folder=`
// in the URL, so descend/back/refresh/bookmark all work with zero
// further requests. A stale `?folder=` id (the owner deleted the folder
// after the link was saved) resolves to root silently — never an error.
// Folders render before the level's videos; a flat share has neither
// tiles nor breadcrumbs and renders exactly as before folders existed.
//
// The resolve query is `staleTime: Infinity` + `retry: false`: a dead share
// stays dead on this mount (no re-resolve loop), and revisiting the page
// within the session reads from cache. A successful unlock flips that
// cached entry to `locked: false` in place (no refetch), so returning
// later in the session renders the grid — not the gate — while the
// unlock cookie is still valid.
//
// The grid reuses `PosterCard` read-only — no `topRightSlot` favorite, no
// `ratingSlot` rating, no progress bar. Cards carry the share scope
// so thumbnails resolve through the pre-auth mirror, and link to
// `/share/:token/watch/:videoId` (task 010's player page). Share members
// are always videos, so `FileCard` has no role here.
//
// The unlock cookie is HttpOnly + `Path=/api/share/`, so the items fetch
// after a successful unlock carries it automatically. If the cookie lapses
// between resolve and items (24h rolling window edge), items answers 401
// and the page falls back to the gate — unlock again and items refetch.
//
// Whole-share search (share-search-ux task 003) overlays the folder
// browse: `?q=` and `?folder=` coexist, a non-empty `q` renders flat
// results across every folder (folder-path labeled), and clearing it
// lands back on the folder the recipient was in. Search is pure
// client-side filtering over the one items payload — the debounced
// input → URL write is the TopbarSearch pattern, and no search state
// ever costs a request.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PosterCard } from "../components/poster-card";
import { SearchBar } from "../components/search-bar";
import { FolderTile } from "../components/folder-tile";
import { ShareResultPathLabel, ShareSearchResults } from "../components/share-search-results";
import { EmptyState } from "../components/ui/empty-state";
import { SkeletonGrid } from "../components/ui/skeleton-grid";
import { ErrorState } from "../components/ui/error-state";
import { Skeleton } from "../components/ui/skeleton";
import { fetchShareItems, resolveShare, unlockShare, type ShareResolveDto } from "../api/shares";
import type { ShareItemsDto } from "../api/types";
import { ApiError } from "../api/client";
import { buildShareTree, searchTree } from "../lib/share-tree";
import { useDebouncedValue } from "../hooks/use-debounced-value";
import { describeError } from "../lib/format";
import { SharedChrome } from "../components/shared-chrome";
import { Button } from "../components/ui/button";
import { Field, Input } from "../components/ui/field";

export function SharePage() {
  const { token } = useParams<{ token: string }>();

  const resolve = useQuery({
    queryKey: ["share", token, "resolve"],
    queryFn: () => resolveShare(token as string),
    enabled: token !== undefined,
    retry: false,
    staleTime: Infinity,
  });

  const canFetchItems = resolve.data !== undefined && !resolve.data.locked;

  const items = useQuery({
    queryKey: ["share", token, "items"],
    queryFn: () => fetchShareItems(token as string),
    enabled: canFetchItems,
    retry: false,
    staleTime: Infinity,
  });

  // Cookie lapsed mid-flight (resolve said unlocked, items says 401) → the
  // gate is the honest state again.
  const itemsLockedOut =
    items.isError && items.error instanceof ApiError && items.error.status === 401;

  if (resolve.isPending) {
    return (
      <SharedChrome>
      <div className="mx-auto max-w-7xl space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-40" />
        <SkeletonGrid />
      </div>
    </SharedChrome>
    );
  }

  if (resolve.isError) {
    const status = resolve.error instanceof ApiError ? resolve.error.status : null;
    if (status === 410) {
      return (
        <TerminalState
          title="This share is no longer available."
          body="The link expired or was revoked by whoever shared it."
        />
      );
    }
    if (status === 404) {
      return (
        <TerminalState
          title="This share doesn't exist."
          body="Double-check the link, or ask whoever sent it for a new one."
        />
      );
    }
    return (
      <SharedChrome>
      <div className="mx-auto max-w-7xl">
        <ErrorState
          title="Couldn't load this share."
          body={describeError(resolve.error)}
          onRetry={() => resolve.refetch()}
        />
      </div>
    </SharedChrome>
    );
  }

  const share = resolve.data;

  if (!canFetchItems || itemsLockedOut) {
    return <PasswordGate title={share.title} token={token as string} />;
  }

  if (items.isPending) {
    return (
      <SharedChrome>
      <div className="mx-auto max-w-7xl space-y-4">
        <h1 className="text-2xl">{share.title}</h1>
        <SkeletonGrid />
      </div>
    </SharedChrome>
    );
  }

  if (items.isError) {
    return (
      <SharedChrome>
      <div className="mx-auto max-w-7xl">
        <ErrorState
          title="Couldn't load this share."
          body={describeError(items.error)}
          onRetry={() => items.refetch()}
        />
      </div>
    </SharedChrome>
    );
  }

  return <ShareBrowse title={share.title} token={token as string} payload={items.data} />;
}

// ---------------------------------------------------------------------------
// Folder browse (share-folders task 005)
// ---------------------------------------------------------------------------

// The unlocked surface: a search field spanning the whole share, plus
// either the folder browse (`?folder=`) or the flat results view (`?q=`).
// All state lives in the URL — one fetch already brought the whole tree,
// so search and every level hop render without touching the network.
const SEARCH_DEBOUNCE_MS = 300;

function ShareBrowse({
  title,
  token,
  payload,
}: {
  title: string;
  token: string;
  payload: ShareItemsDto;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Built once per payload identity — the indexes serve every level hop
  // and every keystroke's search.
  const tree = useMemo(() => buildShareTree(payload), [payload]);
  // Stale ids (owner deleted the folder) fall back to root, silently.
  const folderId = tree.resolveFolderId(searchParams.get("folder"));
  const query = (searchParams.get("q") ?? "").trim();
  const isSearching = query !== "";
  const results = useMemo(
    () => (isSearching ? searchTree(tree, query) : null),
    [tree, query, isSearching],
  );

  // TopbarSearch pattern: raw input is local and instant, the debounced
  // value writes the URL (`replace`, so back/forward isn't noisy), and
  // the URL keeps `folder` alive alongside `q` — clearing the query
  // returns the recipient to the folder they were in.
  const [raw, setRaw] = useState(query);
  const debounced = useDebouncedValue(raw, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    const current = searchParams.get("q") ?? "";
    if (debounced === current) return;
    const params = new URLSearchParams(searchParams);
    if (debounced === "") params.delete("q");
    else params.set("q", debounced);
    setSearchParams(params, { replace: true });
    // Deps are deliberately `[debounced]` alone (TopbarSearch pattern):
    // the write must fire only for a settled input change, never for a
    // URL mutation from elsewhere (folder tiles, clear action, back/
    // forward) — otherwise the stale debounced value would restore `q`
    // right after the navigation dropped it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  // URL → input: deep links, back/forward, and clears stay in sync.
  useEffect(() => {
    setRaw(searchParams.get("q") ?? "");
  }, [searchParams]);

  function handleClearSearch(): void {
    setRaw("");
    const params = new URLSearchParams(searchParams);
    params.delete("q");
    setSearchParams(params, { replace: true });
  }

  const basePath = `/share/${encodeURIComponent(token)}`;
  const levelFolders = tree.childFolders(folderId);
  const levelItems = tree.childItems(folderId);
  const crumbs = folderId === null ? [] : tree.breadcrumbs(folderId);
  const totalItems = payload.items.length;
  const levelIsEmpty = levelFolders.length === 0 && levelItems.length === 0;

  return (
    <SharedChrome>
      <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <h1 className="text-2xl">{title}</h1>
          <p className="mt-1 text-sm text-[var(--color-castify-muted)]">
            {totalItems} {totalItems === 1 ? "video" : "videos"} shared with you
          </p>
        </div>
        <div className="w-full max-w-sm">
          <SearchBar
            value={raw}
            onChange={setRaw}
            label="Search this share"
            placeholder="Find a video…"
          />
        </div>
      </div>
      {results !== null ? (
        <ShareSearchResults query={query} results={results} onClear={handleClearSearch}>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {results.folders.map((folder) => (
              <FolderTile
                key={folder.id}
                name={folder.name}
                folderId={folder.id}
                to={basePath}
                count={tree.childCount(folder.id)}
              />
            ))}
            {results.videos.map((video) => (
              <div key={video.id} className="flex flex-col gap-2">
                <PosterCard
                  video={video}
                  href={`${basePath}/watch/${encodeURIComponent(video.id)}`}
                  scope={{ kind: "share", token }}
                />
                <ShareResultPathLabel path={video.folderPath} rootLabel={title} />
              </div>
            ))}
          </div>
        </ShareSearchResults>
      ) : (
        <>
          {crumbs.length > 0 && (
            <nav aria-label="Folders">
              <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-[var(--color-castify-muted)]">
                <li>
                  <Link
                    to={{ pathname: basePath, search: "" }}
                    className="hover:text-[var(--color-castify-accent)] hover:underline"
                  >
                    {title}
                  </Link>
                </li>
                {crumbs.map((crumb, index) => (
                  <li key={crumb.id} className="flex items-center gap-1.5">
                    <span aria-hidden>/</span>
                    {index === crumbs.length - 1 ? (
                      <span aria-current="page" className="text-[var(--color-castify-foreground)]">
                        {crumb.name}
                      </span>
                    ) : (
                      <Link
                        to={{ pathname: basePath, search: `?folder=${encodeURIComponent(crumb.id)}` }}
                        className="hover:text-[var(--color-castify-accent)] hover:underline"
                      >
                        {crumb.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}
          {levelIsEmpty ? (
            totalItems === 0 ? (
              <EmptyState title="Nothing in this share." body="Whoever shared it hasn't added any videos." />
            ) : (
              <EmptyState title="This folder is empty." body="Nothing was placed in this folder." />
            )
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {levelFolders.map((folder) => (
                <FolderTile
                  key={folder.id}
                  name={folder.name}
                  folderId={folder.id}
                  to={basePath}
                  count={tree.childCount(folder.id)}
                />
              ))}
              {levelItems.map((video) => (
                <PosterCard
                  key={video.id}
                  video={video}
                  href={`${basePath}/watch/${encodeURIComponent(video.id)}`}
                  scope={{ kind: "share", token }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
    </SharedChrome>
  );
}

// ---------------------------------------------------------------------------
// Password gate
// ---------------------------------------------------------------------------

function PasswordGate({ title, token }: { title: string; token: string }) {
  const [password, setPassword] = useState("");
  const queryClient = useQueryClient();

  const unlock = useMutation({
    mutationFn: () => unlockShare(token, password),
    onSuccess: () => {
      // Flip the cached resolve to unlocked in place (setQueryData, not a
      // refetch): unlocking costs exactly one unlock POST + one items GET,
      // and the cache no longer claims `locked` — so returning to this
      // page later in the session (watch → back) renders the grid from
      // cache instead of re-showing the gate for a still-valid cookie.
      // If the cookie has since lapsed, items answers 401 and the gate
      // returns via `itemsLockedOut`.
      queryClient.setQueryData<ShareResolveDto>(["share", token, "resolve"], (old) =>
        old ? { ...old, locked: false } : old,
      );
      void queryClient.invalidateQueries({ queryKey: ["share", token, "items"] });
    },
  });

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    unlock.mutate();
  }

  const errorStatus =
    unlock.error instanceof ApiError ? unlock.error.status : null;

  return (
    <SharedChrome>
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col justify-center">
        <div className="rounded-[var(--radius-card)] border border-[var(--color-castify-rule)] p-8">
          <h1 className="text-2xl">{title}</h1>
          <p className="mt-2 text-sm text-[var(--color-castify-muted)]">
            This share is protected by a password. Enter it to see the videos.
          </p>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <Field
              label="Password"
              error={
                errorStatus === 401
                  ? "Wrong password. Try again."
                  : errorStatus === 429
                    ? "Too many attempts. Please try again in about 15 minutes."
                    : unlock.isError
                      ? `Couldn't unlock this share: ${describeError(unlock.error)}`
                      : undefined
              }
            >
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                autoComplete="current-password"
              />
            </Field>
            <Button
              type="submit"
              className="w-full"
              pending={unlock.isPending}
            >
              {unlock.isPending ? "Unlocking…" : "Unlock"}
            </Button>
          </form>
        </div>
      </div>
    </SharedChrome>
  );
}

// ---------------------------------------------------------------------------
// Terminal states
// ---------------------------------------------------------------------------

// 410/404 render through EmptyState with no action — deliberately no retry.
// A dead link re-fetching on every visit would be a loop with no exit, and
// the resolve query's `retry: false` + `staleTime: Infinity` already pins
// the result for this mount.
function TerminalState({ title, body }: { title: string; body: string }) {
  return (
    <SharedChrome>
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col justify-center">
      <EmptyState title={title} body={body} />
    </div>
    </SharedChrome>
  );
}

